/**
 * Tiny Sentry-style client-side error reporter.
 *
 * Captures: window.onerror, window.onunhandledrejection, and explicit
 * `logClientError(err, ctx?)` calls. POSTs each to /api/client-errors.
 *
 * Throttled (max 1 report / 2 s, max 50 / page lifetime) and deduplicated
 * by message+location so a render loop that throws on every tick doesn't
 * flood the backend.
 *
 * Activated from App.js via `initErrorReporter()` once at app startup.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND_URL}/api/client-errors`;
const PAGE_BUDGET = 50;
const COOLDOWN_MS = 2000;

let lastSentAt = 0;
let sentCount = 0;
const seen = new Set();
let initialized = false;

const viewport = () => {
  try { return `${window.innerWidth}x${window.innerHeight}`; }
  catch (_e) { return undefined; }
};

const safeStringify = (s, max = 8000) => {
  if (s == null) return undefined;
  const str = typeof s === "string" ? s : String(s);
  return str.length > max ? str.slice(0, max) + "…[truncated]" : str;
};

async function send(payload) {
  if (sentCount >= PAGE_BUDGET) return;
  const now = Date.now();
  if (now - lastSentAt < COOLDOWN_MS) return;
  // Dedup signature so the same crash on every render only sends once
  const sig = `${payload.kind}:${payload.message?.slice(0, 200)}:${payload.url}`;
  if (seen.has(sig)) return;
  seen.add(sig);
  lastSentAt = now;
  sentCount += 1;
  try {
    await axios.post(ENDPOINT, payload, {
      withCredentials: true,
      timeout: 5000,
    });
  } catch (_e) {
    // Never throw from the error reporter — that's how Sentry-style libs
    // create infinite loops. Silently drop.
  }
}

/**
 * Manually report an error from anywhere in the app.
 *   logClientError(err, { feature: "doc-upload", docType: "pitch_deck" })
 */
export function logClientError(err, extra) {
  send({
    kind: "manual",
    message: safeStringify(err?.message || err, 2000),
    stack: safeStringify(err?.stack),
    url: window.location?.href,
    user_agent: navigator.userAgent,
    viewport: viewport(),
    extra: extra && Object.keys(extra).length ? extra : undefined,
  });
}

export function initErrorReporter() {
  if (initialized) return;
  initialized = true;

  // Uncaught exceptions (synchronous + React error-boundary rethrows)
  window.addEventListener("error", (ev) => {
    send({
      kind: "window_error",
      message: safeStringify(ev.message || ev.error?.message || "Unknown error", 2000),
      stack: safeStringify(ev.error?.stack),
      url: window.location?.href,
      user_agent: navigator.userAgent,
      viewport: viewport(),
      extra: ev.filename ? {
        file: ev.filename, line: ev.lineno, col: ev.colno,
      } : undefined,
    });
  });

  // Unhandled promise rejections — these are the silent killers
  // ("nothing happens" production failures usually surface here).
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    send({
      kind: "unhandled_rejection",
      message: safeStringify(reason?.message || reason, 2000),
      stack: safeStringify(reason?.stack),
      url: window.location?.href,
      user_agent: navigator.userAgent,
      viewport: viewport(),
    });
  });
}
